import {
  QuoteRequest,
  QuoteResponse,
  ExecuteRequest,
  ExecuteResponse,
  AvailableCoinsResponse,
} from './types';

const BASE_URL = 'https://payamigo-integration.cryptosrvc-staging.com';

class PayAmigoAPIError extends Error {
  public code?: string;
  public status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'PayAmigoAPIError';
    this.code = code;
    this.status = status;
  }
}

export class PayAmigoClient {
  private baseUrl: string;
  private sessionId: string | null = null;

  constructor(baseUrl: string = BASE_URL) {
    this.baseUrl = baseUrl;
  }

  public setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  public clearSessionId(): void {
    this.sessionId = null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireSession: boolean = false
  ): Promise<T> {
      const headers: Record<string, string> = {
        ...(options.headers as Record<string, string>),
      };

      // Add Content-Type only for methods with body (POST, PUT, PATCH)
      if (options.method && ['POST', 'PUT', 'PATCH'].includes(options.method.toUpperCase())) {
        headers['Content-Type'] = 'application/json';
      }

      if (requireSession && this.sessionId) {
        headers['X-Session-Id'] = this.sessionId;
      }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        let errorMessage = `HTTP Error: ${response.status}`;
        let errorCode = response.status.toString();

        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          errorCode = errorData.code || errorCode;
        } catch {
          // If error response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }

        throw new PayAmigoAPIError(errorMessage, errorCode, response.status);
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      if (error instanceof PayAmigoAPIError) {
        throw error;
      }

      if (error instanceof Error) {
        throw new PayAmigoAPIError(
          error.message || 'Network error occurred',
          'NETWORK_ERROR'
        );
      }

      throw new PayAmigoAPIError('An unknown error occurred', 'UNKNOWN_ERROR');
    }
  }

  // Use existing session ID from URL
  public useExistingSession(existingSessionId: string): void {
    this.setSessionId(existingSessionId);
  }

  // Delete session
  public async deleteSession(): Promise<void> {
    await this.request<{}>(
      '/v1/session',
      {
        method: 'DELETE',
      },
      true
    );
    this.clearSessionId();
  }

  // Quote Management
  public async getQuote(params: QuoteRequest): Promise<QuoteResponse> {
    const queryParams = new URLSearchParams({
      cpf_number: params.cpf_number,
      amount: params.amount,
      coin: params.coin,
      ...(params.pix_key && { pix_key: params.pix_key }),
    });

    return this.request<QuoteResponse>(
      `/v1/payments/quote?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      true
    );
  }

  // Payment Execution
  public async executePayment(request: ExecuteRequest): Promise<ExecuteResponse> {
    return this.request<ExecuteResponse>(
      '/v1/payments/execute',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      true
    );
  }

  // Get Available Coins
  public async getAvailableCoins(): Promise<AvailableCoinsResponse> {
    return this.request<AvailableCoinsResponse>(
      '/v1/payments/available-coins',
      {
        method: 'GET',
      },
      true
    );
  }
}

export const payAmigoClient = new PayAmigoClient();

